<template>
  <nav id="leftSidebar" :class="classes">
    <div class="lhs-top-stack">
      <div v-if="brandComponents.length" class="ws-brand-zone">
        <DynamicComponents :components="brandComponents" />
      </div>
      <!-- User/account banner lives at the TOP, under the logo (Toby,
           2026-07-15 — overrides the mockup's bottom-pin after seeing it
           live). Keeps the F-RN-05 workspace row + menu additions. -->
      <LhsHeaderCard>
        <!-- Shell-wide "Report an issue" — reachable from every page for every
             user (no permission gating). Lives in the account menu's consumer
             slot; the component owns its own modal + POST to /me/report-issue. -->
        <template #menu-items="{ closeMenu }">
          <ReportIssueMenuItem :close-menu="closeMenu" />
        </template>
      </LhsHeaderCard>
    </div>

    <div class="lhs-nav-scroll">
      <DynamicComponents :components="restComponents" />

      <!-- Rail-native Admin group: on admin routes the Admin entry expands IN
           PLACE into the settings-tiles sub-groups (LHS-rework Screen B). This
           replaces the deleted 220px AdminSubnav popout column — one nav
           surface. Off admin routes it renders nothing. -->
      <AdminRailGroup :visible-under="adminPrefixes" />
    </div>

    <!-- Pinned foot: AI-tokens mini-meter + top-up line + Settings affordance
         (gated on actually being able to reach /admin — F-RN-04).
         .lhs-nav-scroll above is the flexing middle, so on a short viewport
         the nav scrolls and this never gets pushed off. -->
    <RailUsageMeter :show-settings="hasAdminMenuEntry" />
  </nav>
</template>

<script>
/**
 * TheLeftSidebar (icarus-theme-base) — vertical rail layout:
 *
 *   1. Brand zone (first ImageTile from `components`, if any)
 *   2. LhsHeaderCard (account / user menu + workspace row) — top, under the
 *      logo (Toby 2026-07-15; the mockup's bottom-pin was tried and reverted)
 *   3. Main nav (the rest of `components`) — the three goal clusters
 *      (Pipelines / Outreach / Tools) + the Admin entry
 *   4. AdminRailGroup — the Admin group's sub-sections, expanded in place on
 *      admin routes (replaces the old fixed AdminSubnav column)
 *   5. RailUsageMeter (AI-tokens meter + Settings), pinned to the bottom
 *
 * LHS-rework (2026-07-15): the 220px AdminSubnav popout is DELETED. Admin is now
 * "a place in the rail", not "a mode with its own chrome" — the /admin tile hub
 * stays the launcher; drilling into a tile expands the rail's Admin group in
 * place (AdminRailGroup, fed by the same settings-tiles endpoint the hub uses).
 *
 * Heuristic: the brand block is always the first ImageTile in `components`.
 * Anything else stays in restComponents. Case-insensitive name match.
 */
import DynamicComponents from "@apexl/icarus-core/src/components/partials/DynamicComponents.vue";
import commonProps from '@apexl/icarus-core/src/components/_properties/common';

export default {
  name: 'TheLeftSidebar',
  components: { DynamicComponents },
  props: {
    ...commonProps,
    components: { type: Array, default: () => ([]) },
  },
  computed: {
    // Path prefixes that mark "inside the admin area" so the rail-native Admin
    // group expands. `/admin` covers the config cluster + the tile-grid landing;
    // the rest are the operator/engine surfaces that kept their original
    // (non-/admin) URLs. Bare `/acquire` is deliberately absent — it would also
    // match `/acquire/prospects` and other goal pages; the engine pages are
    // listed by full path instead.
    adminPrefixes() {
      return [
        '/admin',
        '/platform',
        '/outreach/deliverability',
        '/acquire/sources',
        '/acquire/signals',
        '/acquire/scout',
      ];
    },
    brandComponents() {
      const first = (this.components || [])[0];
      if (first && this.isBrandComponent(first)) return [first];
      return [];
    },
    restComponents() {
      const all = this.components || [];
      const first = all[0];
      if (first && this.isBrandComponent(first)) return all.slice(1);
      return all;
    },
    // F-RN-04 — the rail foot's Settings affordance points at /admin, which
    // 403s for any role without at least one admin-area permission. Rather
    // than duplicating that permission check here (a new signal to invent
    // and keep in sync), reuse the one that's ALREADY authoritative: the
    // backend-driven LHS menu itself only contains the Admin leaf when the
    // current user passes its own gate (same convention AdminRailGroup's
    // settings-tiles groups rely on — "the caller can't reach it at all"
    // means the entry is simply absent from the payload). So: cheap, honest
    // signal = "is there an Admin entry anywhere in the menu tree we were
    // already given?" No new endpoint, no duplicated gate logic.
    hasAdminMenuEntry() {
      // The menu payload is deeply nested vendor-shaped JSON whose envelope
      // varies (componentData/customData/props/links…) — the sweep VERIFY
      // pass caught the shape-by-shape walk missing the real nesting and
      // hiding Settings even for super-admin. Serialise once and look for an
      // href-ish "/admin" value instead: shape-proof, and the payload is
      // small. Fail OPEN (show the affordance) — worst case is a 403 door,
      // never a hidden legitimate one.
      try {
        return /"(?:href|to|route|url|link)"\s*:\s*"\/admin\/?"/.test(JSON.stringify(this.components || []));
      } catch {
        return true;
      }
    },
  },
  methods: {
    isBrandComponent(c) {
      const name = c?.name || c?.componentName || c?.type || '';
      return /image[-_ ]?tile/i.test(String(name));
    },
  },
};
</script>

<style lang="scss">
// The rail is a vertical flex column: top stack (brand only, F-RN-05) is
// fixed height, the nav scrolls, the foot cluster (account banner + usage
// meter) pins to the bottom. #leftSidebar itself is already a flex column
// from the vendor layout; ensure the nav takes the middle and scrolls so the
// foot cluster never gets pushed off-screen.
#leftSidebar {
  .lhs-nav-scroll {
    flex: 1 1 auto;
    overflow-y: auto;
    min-height: 0; // allow the flex child to actually scroll
  }

}
</style>
